import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { fetchChannelInfo, isValidYouTubeUrl } from '../utils/youtubeApi';
import { 
  Youtube, 
  Search, 
  Plus, 
  LogOut, 
  ExternalLink,
  Lightbulb,
  Heart,
  ArrowRight,
  Link as LinkIcon,
  Moon,
  Sun,
  Home,
  Zap,
  Settings,
  Edit3,
  Trash2
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Modelagem = ({ onLogout }) => {
  usePageTitle('Modelagem');
  const [inspiracoes, setInspiracoes] = useState([]);
  const [ideias, setIdeias] = useState([]);
  const [canais, setCanais] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewInspiracaoDialog, setShowNewInspiracaoDialog] = useState(false);
  const [showNewIdeiaDialog, setShowNewIdeiaDialog] = useState(false);
  const [showEditInspiracaoDialog, setShowEditInspiracaoDialog] = useState(false);
  const [showEditIdeiaDialog, setShowEditIdeiaDialog] = useState(false);
  const [editingInspiracao, setEditingInspiracao] = useState(null);
  const [editingIdeia, setEditingIdeia] = useState(null);
  const [newInspiracao, setNewInspiracao] = useState({
    nome_canal: '',
    url: '',
    foto_url: '',
    notas: ''
  });
  const [newIdeia, setNewIdeia] = useState({
    conteudo: '',
    canal_id: ''
  });

  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  
  // Get canal from URL params
  const urlParams = new URLSearchParams(location.search);
  const canalFromUrl = urlParams.get('canal');
  
  const [searchingChannel, setSearchingChannel] = useState(false);

  // Auto-fetch channel info when URL is entered
  const handleChannelUrlChange = async (url) => {
    setNewInspiracao({...newInspiracao, url});
    
    if (url && isValidYouTubeUrl(url)) {
      setSearchingChannel(true);
      try {
        const channelInfo = await fetchChannelInfo(url);
        
        setNewInspiracao(prev => ({
          ...prev,
          nome_canal: channelInfo.name,
          foto_url: channelInfo.thumbnailUrl
        }));
        
        toast.success(`Canal encontrado: ${channelInfo.name}`);
        
      } catch (error) {
        console.error('Erro ao buscar canal:', error);
        toast.error('Não foi possível buscar as informações do canal');
      } finally {
        setSearchingChannel(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Set initial canal if provided via URL
  useEffect(() => {
    if (canalFromUrl && canais.length > 0) {
      setNewIdeia(prev => ({...prev, canal_id: canalFromUrl}));
    }
  }, [canalFromUrl, canais]);

  const fetchData = async () => {
    try {
      const [inspiracaoRes, ideiaRes, canalRes] = await Promise.all([
        axios.get(`${API}/inspiracoes`),
        axios.get(`${API}/ideias`),
        axios.get(`${API}/canais`)
      ]);
      
      setInspiracoes(inspiracaoRes.data);
      setIdeias(ideiaRes.data);
      setCanais(canalRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInspiracao = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/inspiracoes`, newInspiracao);
      setInspiracoes([...inspiracoes, response.data]);
      setNewInspiracao({ nome_canal: '', url: '', foto_url: '', notas: '' });
      setShowNewInspiracaoDialog(false);
      toast.success('Inspiração adicionada com sucesso!');
    } catch (error) {
      toast.error('Erro ao adicionar inspiração');
    }
  };

  const handleCreateIdeia = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/ideias`, newIdeia);
      setIdeias([...ideias, response.data]);
      setNewIdeia({ conteudo: '', canal_id: '' });
      setShowNewIdeiaDialog(false);
      toast.success('Ideia adicionada com sucesso!');
    } catch (error) {
      toast.error('Erro ao adicionar ideia');
    }
  };

  const promoverIdeia = async (ideiaId, canalId) => {
    try {
      const ideia = ideias.find(i => i.id === ideiaId);
      if (!ideia) return;

      // Create new video from idea
      const videoData = {
        canal_id: canalId,
        titulo: ideia.conteudo,
        notas: `Promovido de ideia: ${ideia.conteudo}`
      };

      await axios.post(`${API}/videos`, videoData);
      
      // Update idea as promoted
      const updatedIdeias = ideias.map(i => 
        i.id === ideiaId ? { ...i, promovida: true, canal_id: canalId } : i
      );
      setIdeias(updatedIdeias);
      
      toast.success('Ideia promovida para Títulos & Ideias!');
    } catch (error) {
      toast.error('Erro ao promover ideia');
    }
  };

  // Edit and Delete functions for Inspirações
  const handleEditInspiracao = (inspiracao) => {
    setEditingInspiracao(inspiracao);
    setNewInspiracao({
      nome_canal: inspiracao.nome_canal,
      url: inspiracao.url,
      foto_url: inspiracao.foto_url,
      notas: inspiracao.notas
    });
    setShowEditInspiracaoDialog(true);
  };

  const handleUpdateInspiracao = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/inspiracoes/${editingInspiracao.id}`, newInspiracao);
      setInspiracoes(inspiracoes.map(i => i.id === editingInspiracao.id ? response.data : i));
      setNewInspiracao({ nome_canal: '', url: '', foto_url: '', notas: '' });
      setShowEditInspiracaoDialog(false);
      setEditingInspiracao(null);
      toast.success('Inspiração atualizada com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar inspiração');
    }
  };

  const handleDeleteInspiracao = async (inspiracaoId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta inspiração?')) return;
    
    try {
      await axios.delete(`${API}/inspiracoes/${inspiracaoId}`);
      setInspiracoes(inspiracoes.filter(i => i.id !== inspiracaoId));
      toast.success('Inspiração excluída com sucesso!');
    } catch (error) {
      toast.error('Erro ao excluir inspiração');
    }
  };

  // Edit and Delete functions for Ideias
  const handleEditIdeia = (ideia) => {
    setEditingIdeia(ideia);
    setNewIdeia({
      conteudo: ideia.conteudo,
      canal_id: ideia.canal_id || ''
    });
    setShowEditIdeiaDialog(true);
  };

  const handleUpdateIdeia = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/ideias/${editingIdeia.id}`, newIdeia);
      setIdeias(ideias.map(i => i.id === editingIdeia.id ? response.data : i));
      setNewIdeia({ conteudo: '', canal_id: '' });
      setShowEditIdeiaDialog(false);
      setEditingIdeia(null);
      toast.success('Ideia atualizada com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar ideia');
    }
  };

  const handleDeleteIdeia = async (ideiaId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta ideia?')) return;
    
    try {
      await axios.delete(`${API}/ideias/${ideiaId}`);
      setIdeias(ideias.filter(i => i.id !== ideiaId));
      toast.success('Ideia excluída com sucesso!');
    } catch (error) {
      toast.error('Erro ao excluir ideia');
    }
  };

  const filteredInspiracoes = inspiracoes.filter(inspiracao =>
    inspiracao.nome_canal.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inspiracao.notas.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIdeias = ideias.filter(ideia =>
    ideia.conteudo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Dark Planner</h1>
            </div>

            <nav className="flex items-center gap-6">
              <Link 
                to="/" 
                className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Link>
              <Link 
                to="/canais" 
                className={`nav-link ${location.pathname === '/canais' ? 'active' : ''}`}
              >
                <Youtube className="w-4 h-4 mr-2" />
                Canais
              </Link>
              <Link 
                to="/configuracoes" 
                className={`nav-link ${location.pathname === '/configuracoes' ? 'active' : ''}`}
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </Link>
              
              <button
                onClick={toggleTheme}
                className="theme-toggle"
                title={theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Modelagem</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Inspirações e ideias para seus canais</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inspirações Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#0DAEFF]" />
                Inspirações
              </h3>
              <Dialog open={showNewInspiracaoDialog} onOpenChange={setShowNewInspiracaoDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-gradient-to-r from-[#0DAEFF] to-[#3B82F6] hover:from-[#1FB8FF] hover:to-[#4F8FF8] text-white">
                    <Plus className="w-3 h-3 mr-1" />
                    Nova
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Adicionar Canal de Inspiração</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateInspiracao} className="space-y-4">
                    <div>
                      <Label htmlFor="nome_canal">Nome do Canal</Label>
                      <Input
                        id="nome_canal"
                        value={newInspiracao.nome_canal}
                        onChange={(e) => setNewInspiracao({...newInspiracao, nome_canal: e.target.value})}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="url">URL do Canal</Label>
                      <Input
                        id="url"
                        type="url"
                        value={newInspiracao.url}
                        onChange={(e) => handleChannelUrlChange(e.target.value)}
                        required
                        className="mt-1"
                        placeholder="https://youtube.com/@canal"
                        disabled={searchingChannel}
                      />
                      {searchingChannel && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          Buscando informações do canal...
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="notas">Notas</Label>
                      <textarea
                        id="notas"
                        value={newInspiracao.notas}
                        onChange={(e) => setNewInspiracao({...newInspiracao, notas: e.target.value})}
                        className="input-field mt-1"
                        rows="2"
                        placeholder="O que você admira?"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowNewInspiracaoDialog(false)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-[#0DAEFF] hover:bg-[#3B82F6] text-white"
                      >
                        Adicionar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {filteredInspiracoes.length === 0 ? (
                <Card className="compact-card p-6 text-center">
                  <Heart className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Nenhuma inspiração cadastrada
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setShowNewInspiracaoDialog(true)}
                    className="bg-[#0DAEFF] hover:bg-[#3B82F6] text-white"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Primeira Inspiração
                  </Button>
                </Card>
              ) : (
                filteredInspiracoes.map((inspiracao) => (
                  <Card key={inspiracao.id} className="compact-card card-hover">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-[#0DAEFF] to-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Youtube className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                            {inspiracao.nome_canal}
                          </h4>
                          <a
                            href={inspiracao.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <LinkIcon className="w-3 h-3" />
                            Visitar
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditInspiracao(inspiracao)}
                            className="h-6 w-6 p-0 hover:bg-blue-100 dark:hover:bg-blue-900"
                          >
                            <Edit3 className="w-3 h-3 text-blue-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteInspiracao(inspiracao.id)}
                            className="h-6 w-6 p-0 hover:bg-red-100 dark:hover:bg-red-900"
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      {inspiracao.notas && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                          {inspiracao.notas}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Ideias Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#0DAEFF]" />
                Ideias
              </h3>
              <Dialog open={showNewIdeiaDialog} onOpenChange={setShowNewIdeiaDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-gradient-to-r from-[#0DAEFF] to-[#3B82F6] hover:from-[#1FB8FF] hover:to-[#4F8FF8] text-white">
                    <Plus className="w-3 h-3 mr-1" />
                    Nova
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Adicionar Nova Ideia</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateIdeia} className="space-y-4">
                    <div>
                      <Label htmlFor="conteudo">Conteúdo da Ideia</Label>
                      <textarea
                        id="conteudo"
                        value={newIdeia.conteudo}
                        onChange={(e) => setNewIdeia({...newIdeia, conteudo: e.target.value})}
                        required
                        className="input-field mt-1"
                        rows="3"
                        placeholder="Descreva sua ideia..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="canal_id">Canal (opcional)</Label>
                      <select
                        id="canal_id"
                        value={newIdeia.canal_id}
                        onChange={(e) => setNewIdeia({...newIdeia, canal_id: e.target.value})}
                        className="input-field mt-1"
                      >
                        <option value="">Selecione um canal</option>
                        {canais.map(canal => (
                          <option key={canal.id} value={canal.id}>{canal.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowNewIdeiaDialog(false)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-[#0DAEFF] hover:bg-[#3B82F6] text-white"
                      >
                        Adicionar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {filteredIdeias.length === 0 ? (
                <Card className="compact-card p-6 text-center">
                  <Lightbulb className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Nenhuma ideia cadastrada
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setShowNewIdeiaDialog(true)}
                    className="bg-[#0DAEFF] hover:bg-[#3B82F6] text-white"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Primeira Ideia
                  </Button>
                </Card>
              ) : (
                filteredIdeias.map((ideia) => (
                  <Card key={ideia.id} className="compact-card card-hover">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 dark:text-gray-100 mb-2">{ideia.conteudo}</p>
                          <div className="flex items-center gap-2">
                            {ideia.canal_id && (
                              <Badge variant="outline" className="text-xs">
                                {canais.find(c => c.id === ideia.canal_id)?.nome || 'Canal'}
                              </Badge>
                            )}
                            {ideia.promovida && (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                Promovida ✓
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditIdeia(ideia)}
                            className="h-6 w-6 p-0 hover:bg-blue-100 dark:hover:bg-blue-900"
                          >
                            <Edit3 className="w-3 h-3 text-blue-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteIdeia(ideia.id)}
                            className="h-6 w-6 p-0 hover:bg-red-100 dark:hover:bg-red-900"
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </Button>
                          {!ideia.promovida && canais.length > 0 && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline" className="h-6 text-xs">
                                  <ArrowRight className="w-3 h-3" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-sm">
                                <DialogHeader>
                                  <DialogTitle>Promover Ideia</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Selecione o canal:
                                  </p>
                                  <div className="space-y-2">
                                    {canais.map(canal => (
                                      <Button
                                        key={canal.id}
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-xs"
                                        onClick={() => promoverIdeia(ideia.id, canal.id)}
                                      >
                                        <Youtube className="w-3 h-3 mr-2" />
                                        {canal.nome}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Edit Inspiração Dialog */}
      <Dialog open={showEditInspiracaoDialog} onOpenChange={setShowEditInspiracaoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Canal de Inspiração</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateInspiracao} className="space-y-4">
            <div>
              <Label htmlFor="edit_nome_canal">Nome do Canal</Label>
              <Input
                id="edit_nome_canal"
                value={newInspiracao.nome_canal}
                onChange={(e) => setNewInspiracao({...newInspiracao, nome_canal: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit_url">URL do Canal</Label>
              <Input
                id="edit_url"
                type="url"
                value={newInspiracao.url}
                onChange={(e) => handleChannelUrlChange(e.target.value)}
                required
                className="mt-1"
              />
              {searchingChannel && (
                <p className="text-xs text-blue-600 mt-1">🔍 Buscando informações do canal...</p>
              )}
            </div>
            <div>
              <Label htmlFor="edit_foto_url">URL da Foto</Label>
              <Input
                id="edit_foto_url"
                type="url"
                value={newInspiracao.foto_url}
                onChange={(e) => setNewInspiracao({...newInspiracao, foto_url: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit_notas">Notas</Label>
              <textarea
                id="edit_notas"
                value={newInspiracao.notas}
                onChange={(e) => setNewInspiracao({...newInspiracao, notas: e.target.value})}
                className="input-field mt-1"
                rows="3"
                placeholder="Anotações sobre o canal"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditInspiracaoDialog(false);
                  setEditingInspiracao(null);
                  setNewInspiracao({ nome_canal: '', url: '', foto_url: '', notas: '' });
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0DAEFF] hover:bg-[#3B82F6] text-white"
              >
                Atualizar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Ideia Dialog */}
      <Dialog open={showEditIdeiaDialog} onOpenChange={setShowEditIdeiaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Ideia</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateIdeia} className="space-y-4">
            <div>
              <Label htmlFor="edit_conteudo">Conteúdo da Ideia</Label>
              <textarea
                id="edit_conteudo"
                value={newIdeia.conteudo}
                onChange={(e) => setNewIdeia({...newIdeia, conteudo: e.target.value})}
                required
                className="input-field mt-1"
                rows="3"
                placeholder="Descreva sua ideia de vídeo"
              />
            </div>
            <div>
              <Label htmlFor="edit_canal_id">Canal (Opcional)</Label>
              <select
                id="edit_canal_id"
                value={newIdeia.canal_id}
                onChange={(e) => setNewIdeia({...newIdeia, canal_id: e.target.value})}
                className="input-field mt-1"
              >
                <option value="">Selecione um canal</option>
                {canais.map(canal => (
                  <option key={canal.id} value={canal.id}>{canal.nome}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditIdeiaDialog(false);
                  setEditingIdeia(null);
                  setNewIdeia({ conteudo: '', canal_id: '' });
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0DAEFF] hover:bg-[#3B82F6] text-white"
              >
                Atualizar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Modelagem;